/**
 * Build information extracted during the build process
 * This file is generated by scripts/get-version.js
 */

export interface BuildInfo {
  version: string;
  commit: string;
  buildTime: string;
}

/**
 * Validates that the build info has the correct structure
 */
function validateBuildInfo(data: unknown): BuildInfo {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid build info: data must be an object');
  }

  const obj = data as Record<string, unknown>;

  if (typeof obj.version !== 'string') {
    throw new Error('Invalid build info: version must be a string');
  }

  if (typeof obj.commit !== 'string') {
    throw new Error('Invalid build info: commit must be a string');
  }

  if (typeof obj.buildTime !== 'string') {
    throw new Error('Invalid build info: buildTime must be a string');
  }

  return {
    version: obj.version,
    commit: obj.commit,
    buildTime: obj.buildTime,
  };
}

/**
 * Load build info with fallback for missing file
 */
function loadBuildInfo(): BuildInfo {
  try {
    // Dynamic import to handle missing file gracefully
    const buildInfoJson = require('./build-info.json');
    return validateBuildInfo(buildInfoJson);
  } catch (error) {
    // Fallback for development or when build-info.json doesn't exist
    if (process.env.NODE_ENV === 'development') {
      return {
        version: 'dev',
        commit: 'unknown',
        buildTime: new Date().toISOString(),
      };
    }
    throw new Error(
      'build-info.json not found. Run the build process to generate it.',
    );
  }
}

export const buildInfo: BuildInfo = loadBuildInfo();

/**
 * Get the current version string
 * Examples: "v0.1.0", "v0.1.0-3-g1a2b3c4", "dev-1a2b3c4"
 */
export function getVersion(): string {
  return buildInfo.version;
}

/**
 * Get the git commit SHA
 */
export function getCommit(): string {
  return buildInfo.commit;
}

/**
 * Get the build timestamp
 */
export function getBuildTime(): string {
  return buildInfo.buildTime;
}

/**
 * Get all build info as an object for logging
 */
export function getBuildInfo(): BuildInfo {
  return buildInfo;
}
